import { createReducer } from "@reduxjs/toolkit";
import {
	API_DELETE_LABAPPOINTMENT,
	API_DELETE_LABAPPOINTMENT_FAIL,
	API_DELETE_LABAPPOINTMENT_SUCCESS,
	API_GET_ALL_BY_DATE_LABAPPOINTMENT,
	API_GET_ALL_BY_DATE_LABAPPOINTMENT_FAIL,
	API_GET_ALL_BY_DATE_LABAPPOINTMENT_SUCCESS,
	API_GET_FOLDERS_PATIENT_FOR_LABAPPOINTMENT,
	API_GET_FOLDERS_PATIENT_FOR_LABAPPOINTMENT_SUCCESS,
	API_PATCH_LABAPPOINTMENT,
	API_PATCH_LABAPPOINTMENT_FAIL,
	API_PATCH_LABAPPOINTMENT_SUCCESS,
	API_POST_LABAPPOINTMENT,
	API_POST_LABAPPOINTMENT_FAIL,
	API_POST_LABAPPOINTMENT_SUCCESS,
	API_GET_ALL_BETWEEN_DATES_LABAPPOINTMENT,
	API_GET_ALL_BETWEEN_DATES_LABAPPOINTMENT_FAIL,
	API_GET_ALL_BETWEEN_DATES_LABAPPOINTMENT_SUCCESS,
	RESET_LIST,
} from "actions/labAppointment";
import { toast } from "react-toastify";
import { API_VALIDATE_FOLDERS_SUCCESS } from "actions/folder";
import { ErrorAction, errorHandler } from "utils/error";
import { getVersion } from "services/labAppointment";
import { DEFAULT_CLIENT, V2_CLIENT } from "config/clientConstants";

const toNumber = (value) => {
	if (value === null || value === undefined) return value;
	const num = typeof value === 'string' ? parseFloat(value) : Number(value);
	return isNaN(num) ? value : num;
};

const adaptLabAppointmentResponse = (appointment, currentClient) => {
	try {
		if (!appointment || currentClient !== V2_CLIENT) {
			return appointment;
		}

		const adapted = { ...appointment };

		const fieldMap = {
			pickenDohem: 'picken_dohem',
			lastRepetition: 'last_repetition',
		};

		Object.keys(fieldMap).forEach((camelKey) => {
			if (adapted[camelKey] !== undefined && adapted[fieldMap[camelKey]] === undefined) {
				adapted[fieldMap[camelKey]] = adapted[camelKey];
			}
		});

		if (adapted.patient && typeof adapted.patient === 'object' && adapted.patient.id !== undefined) {
			if (adapted.patient_id === undefined) {
				adapted.patient_id = toNumber(adapted.patient.id);
			}

			// UI expects legacy patient keys in some LabAppointment screens.
			if (adapted.patient.firstname === undefined && adapted.patient.firstName !== undefined) {
				adapted.patient.firstname = adapted.patient.firstName;
			}
			if (adapted.patient.surname === undefined && adapted.patient.birthName !== undefined) {
				adapted.patient.surname = adapted.patient.birthName;
			}
			if (adapted.patient.weddingName === undefined && adapted.patient.marriedName !== undefined) {
				adapted.patient.weddingName = adapted.patient.marriedName;
			}

			// Some screens expect `patient.folders`; if backend returns `appointment.folder`, expose it.
			if (adapted.patient.folders === undefined && adapted.folder) {
				adapted.patient.folders = [adapted.folder];
			}
		}

		if (adapted.center && typeof adapted.center === 'object' && adapted.center.id !== undefined) {
			if (adapted.center_id === undefined) {
				adapted.center_id = toNumber(adapted.center.id);
			}
		}

		if (adapted.box && typeof adapted.box === 'object' && adapted.box.id !== undefined) {
			if (adapted.box_id === undefined) {
				adapted.box_id = toNumber(adapted.box.id);
			}
		}

		if (adapted.folder && typeof adapted.folder === 'object' && adapted.folder.id !== undefined) {
			if (adapted.folder_id === undefined) {
				adapted.folder_id = toNumber(adapted.folder.id);
			}
		}

		return adapted;
	} catch (error) {
		console.error('Error in adaptLabAppointmentResponse:', error);
		return appointment;
	}
};

const adaptLabAppointmentPayloadResponse = (action, actionType) => {
	try {
		const payload = action.payload || {};
		const data = payload.data;
		const currentClient = action.meta?.previousAction?.payload?.client || getVersion(actionType).currentClient;

		if (!data) {
			return payload;
		}

		if (currentClient === V2_CLIENT) {
			if (Array.isArray(data)) {
				const adaptedData = data.map((item) => adaptLabAppointmentResponse(item, currentClient));
				return {
					...payload,
					data: adaptedData,
				};
			} else if (typeof data === 'object' && data !== null) {
				if (data.results && Array.isArray(data.results)) {
					const adaptedResults = data.results.map((item) => adaptLabAppointmentResponse(item, currentClient));
					return {
						...payload,
						data: {
							...data,
							results: adaptedResults,
						},
					};
				} else if (data.labAppointment) {
					const adaptedLabAppointment = adaptLabAppointmentResponse(data.labAppointment, currentClient);
					return {
						...payload,
						data: {
							...data,
							labAppointment: adaptedLabAppointment,
						},
					};
				} else {
					const adaptedData = adaptLabAppointmentResponse(data, currentClient);
					return {
						...payload,
						data: adaptedData,
					};
				}
			}
		}

		return payload;
	} catch (error) {
		console.error('Error in adaptLabAppointmentPayloadResponse:', error);
		return action.payload || {};
	}
};

const initialState = {
	loading: false,
	error: null,
	list: [],
	pages: 0,
	listTotalCount: 0,
};

let index;
const labAppointment = createReducer(initialState, {
	/** GET ALL **/
	[API_GET_ALL_BY_DATE_LABAPPOINTMENT]: (state, action) => {
		state.loading = true;
	},
	[API_GET_ALL_BY_DATE_LABAPPOINTMENT_FAIL]: (state, action) => {
		errorHandler("Rendez-vous", ErrorAction.READ, action.error);
		state.loading = false;
	},
	[API_GET_ALL_BY_DATE_LABAPPOINTMENT_SUCCESS]: (state, action) => {
		state.loading = false;
		const adaptedPayload = adaptLabAppointmentPayloadResponse(action, API_GET_ALL_BY_DATE_LABAPPOINTMENT);
		state.list = adaptedPayload.data || [];
	},

	/** GET ALL **/
	[API_GET_ALL_BETWEEN_DATES_LABAPPOINTMENT]: (state, action) => {
		//state.loading = true;
	},
	[API_GET_ALL_BETWEEN_DATES_LABAPPOINTMENT_FAIL]: (state, action) => {
		errorHandler("Rendez-vous", ErrorAction.READ, action.error);
		state.loading = false;
		state.list = [];
		state.pages = 0;
		state.listTotalCount = 0;
	},
	[API_GET_ALL_BETWEEN_DATES_LABAPPOINTMENT_SUCCESS]: (state, action) => {
		state.loading = false;
		const adaptedPayload = adaptLabAppointmentPayloadResponse(action, API_GET_ALL_BETWEEN_DATES_LABAPPOINTMENT);
		if (adaptedPayload.data) {
			state.list = adaptedPayload.data.results || [];
			state.pages = adaptedPayload.data.numberOfPages || 0;
			state.page = adaptedPayload.data.page || 1;
			state.listTotalCount = adaptedPayload.data.totalCount || 0;
		}
	},

	/** POST **/
	[API_POST_LABAPPOINTMENT]: (state, action) => {
		state.loading = true;
	},
	[API_POST_LABAPPOINTMENT_FAIL]: (state, action) => {
		errorHandler("Rendez-vous", ErrorAction.CREATE, action.error);
		state.loading = false;
	},
	[API_POST_LABAPPOINTMENT_SUCCESS]: (state, action) => {
		state.loading = false;
		const adaptedPayload = adaptLabAppointmentPayloadResponse(action, API_POST_LABAPPOINTMENT);
		
		// Check if actual list is the good day of the new appointment
		if (state.list.length !== 0 && adaptedPayload.data) {
			const labAppointment = adaptedPayload.data.labAppointment || adaptedPayload.data;
			if (labAppointment && state.list[0]._date === labAppointment._date) {
				state.list.push(labAppointment);
			}
		}
	},

	/** PATCH **/
	[API_PATCH_LABAPPOINTMENT]: (state, action) => {
		state.loading = true;
	},
	[API_PATCH_LABAPPOINTMENT_FAIL]: (state, action) => {
		errorHandler("Rendez-vous", ErrorAction.UPDATE, action.error);
		state.loading = false;
	},
	[API_PATCH_LABAPPOINTMENT_SUCCESS]: (state, action) => {
		state.loading = false;
		const adaptedPayload = adaptLabAppointmentPayloadResponse(action, API_PATCH_LABAPPOINTMENT);
		
		if (adaptedPayload.data) {
			const adaptedData = adaptedPayload.data;
			if (adaptedData._date !== action.meta.previousAction.payload.currentDate) {
				const index = state.list.findIndex((i) => i.id === adaptedData.id);
				if (index >= 0) {
					state.list.splice(index, 1);
				}
			} else {
				const index = state.list.findIndex((i) => i.id === adaptedData.id);
				if (index >= 0) {
					state.list[index] = adaptedData;
				}
			}
		}

		toast.success("Rendez vous modifié");
	},

	/** PATCH **/
	[API_DELETE_LABAPPOINTMENT]: (state, action) => {
		state.loading = true;
	},
	[API_DELETE_LABAPPOINTMENT_FAIL]: (state, action) => {
		errorHandler("Rendez-vous", ErrorAction.DELETE, action.error);
		state.loading = false;
	},
	[API_DELETE_LABAPPOINTMENT_SUCCESS]: (state, action) => {
		toast.success("Rendez vous supprimé");

		state.loading = false;
		state.list.splice(
			state.list.findIndex((i) => i.id === action.meta.previousAction.payload.id),
			1
		);
	},

	/** GET PATIENT FOLDERS **/
	[API_GET_FOLDERS_PATIENT_FOR_LABAPPOINTMENT]: (state, action) => {},

	[API_GET_FOLDERS_PATIENT_FOR_LABAPPOINTMENT_SUCCESS]: (state, action) => {
		index = state.list.findIndex((elt) => action.meta.previousAction.payload.id === elt.id);
		state.list[index].folders = action.payload.data;
	},

	[API_VALIDATE_FOLDERS_SUCCESS]: (state, action) => {
		action.payload.data.labAppointmentsDone.forEach((labAp) => {
			index = state.list.findIndex((elt) => labAp.id === elt.id);
			if (index > -1) {
				action.payload.data.foldersDone.forEach((folderDone) => {
					state.list[index].folders = state.list[index].folders.filter((folder) => folder.id !== folderDone);
				});
			}
		});
	},
	[RESET_LIST]: (state, action) => {
		return {
			...state,
			list: [],
		};
	},
});

export default labAppointment;
